import React, { useState } from "react";
import {
  useFormContext,
  Controller,
  useForm,
  FormProvider,
} from "react-hook-form";
import makeAnimated from "react-select/animated";
import Datepicker from "react-tailwindcss-datepicker";
import {
  UseGetRfiListForFilters,
  usePostAssessmentsRequests,
} from "../custom-hooks/queries";
import ComboBox from "../components/ComboBox";
import { countries } from "../utils/constants";
import { Autocomplete, LoadScript } from "@react-google-maps/api";
import ProviderContactDrawer from "../components/ProviderContactDrawer";
import { yupResolver } from "@hookform/resolvers/yup";
import { newProviderValidationSchema } from "../Schema/newProviderSchema";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import NotificationModal from "../components/NotificationModal";
import { useDispatch } from "react-redux";
import { setDrawer } from "../redux-store/features/layoutSlice";
import MultiSelectWithController from "../components/MultiSelectWithController";
import HttpsUrlInput from "../components/HttpsUrlInput";
import { fortyFiveDays } from "../utils/helperMethods";
import { makeFoundationAllSelection } from "../utils/foundationScope";
import FoundationLockedMultiSelect from "../components/FoundationLockedMultiSelect";

const animatedComponents = makeAnimated();
const libraries = ["places"]; // We need 'places' for Google Places Autocomplete

export const NewProvider = () => {
  const methods = useForm({
    resolver: yupResolver(newProviderValidationSchema),
    mode: "onTouched",
    defaultValues: {
      locationType: "primary",
    },
  });
  const newGuid = uuidv4();

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
    register,
    control,
  } = methods;
  const { data: rfis } = UseGetRfiListForFilters();
  const optionsForMultiSelect = rfis?.map((item) => ({
    value: item?.rfi_id,
    label: item?.rfi_name,
  }));
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { mutate, error, isError } = usePostAssessmentsRequests();

  const [autocomplete1, setAutocomplete1] = useState(null);
  const [autocomplete2, setAutocomplete2] = useState(null);
  const [autocomplete3, setAutocomplete3] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const dispatch = useDispatch();

  const handleOnLoad1 = (autocompleteInstance) => {
    setAutocomplete1(autocompleteInstance);
  };
  const handleOnLoad2 = (autocompleteInstance) => {
    setAutocomplete2(autocompleteInstance);
  };
  const handleOnLoad3 = (autocompleteInstance) => {
    setAutocomplete3(autocompleteInstance);
  };
  const locationType = watch("locationType"); // Watch the locationType field to determine which is selected
  const [primaryAddress, setPrimaryAddress] = useState("");
  const [additionalAddress, setAdditionalAddress] = useState("");
  const [otherAddress, setOtherAddress] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [formData, setFormData] = useState({
    mainContactName: "",
    mainContactEmail: "",
  });

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#AB54DB" : "white", // Purple background on hover
      color: state.isFocused ? "white" : "black", // White text on hover for better contrast
    }),
  };

  const onSubmit = (data) => {
    const transformedCapabilities = data?.serviceCategories?.map((service) => ({
      rfi_id: service.value,
      rfi_name: service.label,
    }));

    // const fullName =
    //   data?.providerContactDetails.first_name +
    //   data?.providerContactDetails.last_name;
    mutate(
      {
        providerId: newGuid,
        providerType: "", // Add a value if necessary
        isPrimaryLocationRequired:
          data?.locationType === "primary" ? true : false || false,
        isSecondaryLocationRequired:
          data?.locationType === "secondary" ? true : false || false,
        secondaryLocationAddress: data?.secondaryLocationTextBox || "",
        capabilities: transformedCapabilities, // Ensure this is correctly formatted
        desiredInitialDraftDate: data?.draftDate.startDate || "",
        additionalDetails: data?.additionalDetails || "",
        isMultiSponsorAssessment: true,
        providerName: data?.providerName || "", // Add a value if necessary
        providerMainContactName: data?.mainContactName || "", // Add a value if necessary
        providerMainContactEmail: data?.mainContactEmail || "", // Add a value if necessary
        providerUrl: data?.providerUrl || "", // Add a value if necessary
        providerAddress1: data?.primaryLocationTextBox || "", // Add a value if necessary
        providerCity: data?.city || "", // Add a value if necessary
        providerState: data?.state || "", // Add a value if necessary
        providerZip: data?.zipCode || "", // Add a value if necessary
        providerCountry: data?.country?.name || "", // Add a value if necessary
        providerPhoneNumber: data?.providerPhoneNumber || "", // Add a value if necessary
        providerAdditionalAddresses: data?.additionalAddress || "", // Add a value if necessary
        providerAddress2: data?.providerAddress2 || "", // Add a value if necessary
        onsite: data?.onsiteRemote.onsite || false,
        remote: data?.onsiteRemote.remote || false,
        otherAddress: data?.otheryLocationTextBox,
        is_provider_new: true,
      },
      {
        onSuccess: () => {
          setMessage(
            "Your request has been submitted to the admin for review. You will be notified once it is processed."
          );
          dispatch(setDrawer(true));
          setShow(true);
          setTimeout(() => {
            navigate("/Assessment/History");
          }, [3000]);
          reset();
        },
        onError: () => {
          setShow(true);
          setMessage("Something went wrong!");
        },
      }
    );
  };
  return (
    <div className="px-5">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Assessment Location */}
          <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
            <h1 className="font-semibold">Create New Vendor</h1>
            <p className="text-sm text-gray-600">
              Complete the information below to request a new provider to be
              added to the Diligent Qualification Platform.
            </p>
          </div>
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 border-b border-gray-300 py-5 lg:py-6">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Vendor Name
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <input
                  type="text"
                  {...register("providerName")}
                  className="ring-0 border-gray-300 rounded-md text-sm  w-[300px] lg:w-[400px] xl:w-[500px] "
                  placeholder="Enter Vendor Name"
                />
              </span>
              {errors.providerName && (
                <p className="text-red-500 text-sm">
                  {errors.providerName.message}
                </p>
              )}
            </div>
          </div>

          {/* Service Categories */}
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0 border-b border-gray-300 py-5 lg:py-6">
            <h1 className="block text-sm font-medium text-gray-900">
              Main Contact Name
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <input
                  placeholder="Enter Contact Name"
                  type="text"
                  {...register("mainContactName")}
                  className="ring-0 border-gray-300 rounded-md text-sm  w-[300px] lg:w-[400px] xl:w-[500px] "
                />
              </span>
              {errors.mainContactName && (
                <p className="text-red-500 text-sm">
                  {errors.mainContactName.message}
                </p>
              )}
            </div>
          </div>
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0 border-b border-gray-300 py-5 lg:py-6">
            <h1 className="block text-sm font-medium text-gray-900">
              Main Contact Email
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <input
                  type="text"
                  placeholder="Enter Contact Email"
                  {...register("mainContactEmail")}
                  className="ring-0 border-gray-300 rounded-md text-sm  w-[300px] lg:w-[400px] xl:w-[500px] "
                />
              </span>
              {errors.mainContactEmail && (
                <p className="text-red-500 text-sm">
                  {errors.mainContactEmail.message}
                </p>
              )}
            </div>
          </div>
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0 border-b border-gray-300 py-5 lg:py-6">
            <h1 className="block text-sm font-medium text-gray-900">
              Vendor URL
            </h1>
            <div className="flex flex-col space-y-3 w-[300px] lg:w-[400px] xl:w-[500px]">
              <HttpsUrlInput
                name="providerUrl"
                label=""
                placeholder="www.vendorwebsite.com"
              />
            </div>
          </div>

          <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
            <h1 className="font-semibold">Primary Address Information</h1>
            <p className="text-sm text-gray-600">
              Please provide the provider's headquarters address.
            </p>
          </div>
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 border-b border-gray-300 py-5 lg:py-6">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Primary Address
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <span className="space-x-3">
                  <Controller
                    name="primaryLocationTextBox" // Register field with react-hook-form
                    control={control}
                    render={({ field }) => (
                      <LoadScript
                        googleMapsApiKey="AIzaSyCmiTZu8oGRJ-yZVYPi7CLnzYWsMi-wkz8"
                        libraries={libraries}
                      >
                        <Autocomplete
                          onLoad={handleOnLoad1}
                          onPlaceChanged={() => {
                            if (autocomplete1) {
                              const place = autocomplete1.getPlace();

                              // Add safety checks
                              const formattedAddress =
                                place?.formatted_address || "";
                              const geometry = place?.geometry;

                              field.onChange(formattedAddress); // Update react-hook-form field
                              setPrimaryAddress(formattedAddress);

                              if (geometry) {
                                const { lat, lng } = geometry.location;
                                setCoordinates({
                                  lat: lat(),
                                  lng: lng(),
                                });
                              }
                            }
                          }}
                        >
                          <input
                            {...field} // Attach react-hook-form field
                            type="text"
                            placeholder="Enter an address"
                            className="border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] mt-5"
                          />
                        </Autocomplete>
                      </LoadScript>
                    )}
                  />
                </span>
              </span>
            </div>
          </div>
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 border-b border-gray-300 py-5 lg:py-6">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Additional Addresses
            </h1>
            <div className="flex flex-col space-y-3">
              <span className="space-x-3">
                <Controller
                  name="secondaryLocationTextBox" // Register field with react-hook-form
                  control={control}
                  render={({ field }) => (
                    <LoadScript
                      googleMapsApiKey="AIzaSyCmiTZu8oGRJ-yZVYPi7CLnzYWsMi-wkz8"
                      libraries={libraries}
                    >
                      <Autocomplete
                        onLoad={handleOnLoad2}
                        onPlaceChanged={() => {
                          if (autocomplete2) {
                            const place = autocomplete2.getPlace();

                            field.onChange(place.formatted_address || ""); // Update react-hook-form field
                            setOtherAddress(place.formatted_address || "");
                            if (place.geometry) {
                              const { lat, lng } = place.geometry.location;
                              setCoordinates({
                                lat: lat(),
                                lng: lng(),
                              });
                            }
                          }
                        }}
                      >
                        <input
                          {...field} // Attach react-hook-form field
                          type="text"
                          placeholder="Enter an address"
                          className="border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] mt-5"
                        />
                      </Autocomplete>
                    </LoadScript>
                  )}
                />
              </span>
            </div>
          </div>

          {/* Service Categories */}

          <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
            <h1 className="font-semibold">
              Request Audited Assessment Report (VQA)
            </h1>
            <p className="text-sm text-gray-600">
              Complete the information below to request a formal audited
              assessment report (VQA).
            </p>
          </div>
          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
            <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
              Assessment Location*
            </h1>
            <div className="flex flex-col space-y-5">
              <span className="space-x-3">
                <input
                  type="radio"
                  defaultChecked={locationType === "primary"}
                  {...register("locationType")}
                  value={"primary"}
                  className="text-indigo-600 focus:ring-indigo-600 focus:ring-offset-2"
                  checked={locationType === "primary"} // Set checked state based on locationType value
                />
                <label className="font-medium text-sm text-gray-900">
                  Primary Location
                </label>
                {errors.locationType && (
                  <p className="text-red-500 text-sm mt-3 ml-10">
                    {errors.locationType.message}
                  </p>
                )}
                <span className="">
                  <Controller
                    name="primaryLocationTextBox" // Register field with react-hook-form
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field} // Attach react-hook-form field
                        type="text"
                        placeholder="Enter an address"
                        className="border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[480px] ml-4 mt-5"
                        disabled={true}
                        // value={providerDetails?.primary_Location}
                      />
                    )}
                  />

                  {errors.primaryLocationTextBox && (
                    <p className="text-red-500 text-sm ml-8 mt-2">
                      {errors.primaryLocationTextBox.message}
                    </p>
                  )}
                </span>
              </span>
              <span className="space-x-3">
                <input
                  type="radio"
                  {...register("locationType")}
                  className="text-indigo-600 focus:ring-indigo-600 focus:ring-offset-2"
                  value="secondary" // Set value to "secondary" for this radio button
                  checked={locationType === "secondary"} // Set checked state based on locationType value
                />
                <label className="font-medium text-sm text-gray-900">
                  Secondary Location(s)
                </label>
                <span className="space-x-3">
                  <Controller
                    name="secondaryLocationTextBox" // Register field with react-hook-form
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field} // Attach react-hook-form field
                        type="text"
                        placeholder="Enter an address"
                        className="border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[480px] ml-4 mt-5"
                        disabled={true}
                        // value={providerDetails?.secondary_Location}
                      />
                    )}
                  />
                </span>
              </span>
              <span className="space-x-3">
                <input
                  type="radio"
                  {...register("locationType")}
                  className="text-indigo-600 focus:ring-indigo-600 focus:ring-offset-2"
                  value="other" // Set value to "secondary" for this radio button
                  checked={locationType === "other"} // Set checked state based on locationType value
                />
                <label className="font-medium text-sm text-gray-900">
                  Other(s)
                </label>
                <span className="space-x-3">
                  <Controller
                    name="otheryLocationTextBox" // Register field with react-hook-form
                    control={control}
                    render={({ field }) => (
                      <LoadScript
                        googleMapsApiKey="AIzaSyCmiTZu8oGRJ-yZVYPi7CLnzYWsMi-wkz8"
                        libraries={libraries}
                      >
                        <Autocomplete
                          onLoad={handleOnLoad3}
                          onPlaceChanged={() => {
                            if (autocomplete3) {
                              const place = autocomplete3.getPlace();

                              field.onChange(place.formatted_address || ""); // Update react-hook-form field
                              setOtherAddress(place.formatted_address || "");
                              if (place.geometry) {
                                const { lat, lng } = place.geometry.location;
                                setCoordinates({
                                  lat: lat(),
                                  lng: lng(),
                                });
                              }
                            }
                          }}
                        >
                          <input
                            {...field} // Attach react-hook-form field
                            type="text"
                            placeholder="Enter an address"
                            className="border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[480px] ml-4 mt-5"
                          />
                        </Autocomplete>
                      </LoadScript>
                    )}
                  />
                </span>
              </span>
            </div>
          </div>
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
            <h1 className="block text-sm font-medium text-gray-900">Contact</h1>
            <div className="md:w-[390px]">
              {/* Controller for Onsite/Remote Dropdown */}
              <Controller
                name="providerContactDetails" // Matches the Yup schema
                control={control}
                render={({ field }) => (
                  <ComboBox
                    // data={providerDetails?.provider_user}
                    placeHolder="Select..."
                    setSelected={(value) => {
                      // Update the value of onsiteRemote
                      setValue("providerContactDetails", value);
                    }}
                    selected={watch("providerContactDetails")}
                    width={"w-[500px]"}
                    inputWidth={"w-[500px]"}
                  />
                )}
              />
              {/* <span
                onClick={() => setContactOpen(true)}
                className="text-sm text-blue-500 ml-[5px] mt-2 cursor-pointer"
              >
                Add Contact
              </span> */}
              {/* Display validation errors */}
              {errors.onsiteRemote && (
                <p className="text-red-500 text-sm">
                  {errors.onsiteRemote.message}
                </p>
              )}
            </div>
          </div>
          {/* Service Categories */}
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
            <h1 className="block text-sm font-medium text-gray-900">
              Service Categories*
            </h1>
            <div>
              <Controller
                name="serviceCategories"
                control={control}
                defaultValue={[makeFoundationAllSelection()]}
                render={({ field }) => (
                  <FoundationLockedMultiSelect
                    {...field}
                    closeMenuOnSelect={false}
                    animatedComponents={animatedComponents}
                    options={optionsForMultiSelect}
                    value={field.value}
                    onChange={(selected) => {
                      setValue("serviceCategories", selected);
                    }}
                    isClearable={field.value?.length > 1}
                    className="w-[330px] lg:w-[400px] xl:w-[500px]"
                    styles={customStyles}
                  />
                )}
              />

              {errors.serviceCategories && (
                <p className="text-red-500 text-sm">
                  {errors.serviceCategories.message}
                </p>
              )}
            </div>
          </div>
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
            <h1 className="block text-sm font-medium text-gray-900">
              Onsite / Remote*
            </h1>
            <div className="md:w-[390px]">
              {/* Controller for Onsite/Remote Dropdown */}
              <Controller
                name="onsiteRemote" // Matches the Yup schema
                control={control}
                defaultValue={{ onsite: false, remote: true }} // Default value is Remote
                render={({ field }) => (
                  <ComboBox
                    data={[
                      { value: false, name: "Remote" },
                      { value: true, name: "Onsite" },
                    ]}
                    placeHolder="Select..."
                    setSelected={(value) => {
                      // Update the value of onsiteRemote dynamically
                      setValue("onsiteRemote", {
                        onsite: value.name === "Onsite",
                        remote: value.name === "Remote",
                      });
                    }}
                    selected={
                      field.value.onsite
                        ? { value: true, name: "Onsite" }
                        : { value: false, name: "Remote" }
                    }
                    width="w-[500px]"
                    inputWidth="w-[500px]"
                  />
                )}
              />

              {/* Display validation errors */}
              {errors.onsiteRemote && (
                <p className="text-red-500 text-sm">
                  {errors.onsiteRemote.message}
                </p>
              )}
            </div>
          </div>

          {/* Desired Initial Draft Date */}
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
            <h1 className="block text-sm font-medium text-gray-900">
              Desired Initial Draft Date*
            </h1>
            <div className="w-[300px] lg:w-[400px] xl:w-[500px]">
              <Controller
                name="draftDate"
                control={control}
                render={({ field }) => (
                  <div className="relative w-full">
                    <Datepicker
                      {...field}
                      name="draftDate"
                      onChange={field.onChange}
                      placeholder="Select Date"
                      useRange={false}
                      displayFormat="MMMM DD, YYYY"
                      startFrom={fortyFiveDays} // 👈 this ensures calendar starts from the enabled date
                      asSingle
                      minDate={fortyFiveDays} // Restrict to current or upcoming dates
                      containerClassName="datepicker-dropdown w-[330px] lg:w-[400px] xl:w-[500px]"
                      popperClassName="datepicker-dropdown w-[330px] lg:w-[400px] xl:w-[500px] p-2 border border-gray-300 rounded-lg shadow-lg bg-white grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto"
                      inputClassName="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                    />
                  </div>
                )}
              />
              {errors.draftDate && (
                <p className="text-red-500 text-sm mt-2">
                  {errors.draftDate.startDate.message}
                </p>
              )}
            </div>
          </div>

          {/* Additional Details */}
          <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
            <h1 className="block text-sm font-medium text-gray-900">
              Additional Details
            </h1>
            <div>
              <textarea
                {...register("additionalDetails")}
                placeholder="Provide additional request details"
                className="border-gray-300 rounded-md text-sm  w-[330px] lg:w-[400px] xl:w-[500px] "
              />
              <p className="text-sm text-gray-500">
                Please provide additional details around this request
              </p>
              {errors.additionalDetails && (
                <p className="text-red-500 text-sm">
                  {errors.additionalDetails.message}
                </p>
              )}
            </div>
          </div>

          {/* Multi-Sponsor Checkbox */}

          <div className="mt-10 flex items-center justify-end gap-x-6 border-t py-2 px-5">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault(); // Prevent page refresh
                handleSubmit((data) => onSubmit(data))(); // Only submit valid data
              }}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Submit
            </button>
          </div>
        </form>
      </FormProvider>
      <ProviderContactDrawer
        open={contactOpen}
        setOpen={setContactOpen}
        register={register}
        setFormData={setFormData}
        formData={formData}
        watch={watch}
      />
      <NotificationModal
        setOpen={setShow}
        open={show}
        title={isError ? "Error" : "Assessment Request Sent Successfully!"}
        desc={isError ? error?.response?.data?.message : message}
        error={isError}
      />{" "}
    </div>
  );
};
